/**
 * HTTP Service
 * Acts as an interceptor for all API calls to automatically inject the auth token,
 * handle common errors (like 401 Unauthorized), and manage base URLs.
 */

import { CONFIG } from "./config";
import { getAuthToken, clearAuthData } from "./storage.service";

/**
 * Standard error interface for API responses
 */
export interface HttpError {
    message: string;
    status?: number;
}

/**
 * Perform an HTTP request with automatic token injection and error handling.
 * Acts as a wrapper around the native Javascript `fetch` API.
 * 
 * @param endpoint The API endpoint (e.g. '/api/documents')
 * @param options Traditional fetch options (method, body, headers, etc.)
 * @returns Promise resolving to the expected JSON or throwing an HttpError
 */
export async function httpClient<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T> {
    const token = getAuthToken();

    // Initialize headers if not provided
    const headers = new Headers(options.headers || {});

    // Automatically inject Bearer Token if available
    if (token) {
        headers.set("Authorization", `Bearer ${token}`);
    }

    // Set default accept header
    if (!headers.has("accept")) {
        headers.set("accept", "*/*");
    }

    // Automatically set Content-Type to JSON if the body is a string
    // and we are not sending FormData (which needs the browser to set boundaries)
    if (options.body && typeof options.body === "string" && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
    }

    const url = `${CONFIG.BACKEND_API_URL}${endpoint}`;

    try {
        const response = await fetch(url, {
            ...options,
            headers,
        });

        if (!response.ok) {
            // Handle automatic logout on 401 Unauthorized or 403 Forbidden
            if (response.status === 401 || response.status === 403) {
                console.warn("🔒 Unauthorized access detected. Clearing session.");
                clearAuthData();
                // Redirect to login if in a browser-like environment where it makes sense
                if (typeof window !== "undefined" && window.location.pathname.includes("dashboard")) {
                    // You might prefer to handle redirecting in the UI layer instead of the service
                    // window.location.href = "taskpane.html"; 
                }
            }

            await throwParsedError(response);
        }

        // Try parsing as JSON. Some endpoints might return empty bodies (like 204 No Content).
        const text = await response.text();
        if (!text) return null as unknown as T;

        return JSON.parse(text) as T;

    } catch (error) {
        // If it's already our custom HttpError, re-throw
        if ((error as HttpError).status) {
            throw error;
        }

        // Network error (Server down, CORS, no internet)
        console.error("HTTP Client Error:", error);
        throw {
            message: "Erreur de connexion au serveur.",
        } as HttpError;
    }
}

/**
 * Standardize error format from backend
 */
async function throwParsedError(response: Response): Promise<never> {
    let errorMessage = "Une erreur s'est produite";

    try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
    } catch (parseError) {
        try {
            const errorText = await response.text();
            if (errorText) {
                errorMessage = errorText;
            }
        } catch (textError) {
            // Keep default message
        }
    }

    throw {
        message: errorMessage,
        status: response.status,
    } as HttpError;
}
